import React, { ChangeEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Typography } from '@mui/material';

import {
  addMinutesToTime,
  normalizeLocation,
  parseTimeString,
} from '../../utils/helper';
import { calendarDays } from '../../utils/calendar';
import { knownCityMap } from '../../utils/knownCity';
import AnotherCity from '../AnotherCity';
import DateInput from '../DateInput';
import Footer from '../Footer';
import Night from '../Night';
import Promotion from '../Promotion';
import Share from '../Share';
import Timings from '../Timings';

import './Choghadiya.scss';

export interface AstronomyData {
  sunriseTime: string | undefined;
  sunsetTime: string | undefined;
}

export interface Locality {
  city: string;
  region: string;
  country: string;
}

const Choghadiya = () => {
  // State to hold today's date in ISOString
  const [today, setToday] = useState(() => {
    const currentDate = new Date();
    return currentDate.toISOString().split('T')[0];
  });
  // State to hold tomorrow's date in ISOString
  const [tomorrow, setTomorrow] = useState('');
  // State to hold currently selected date
  const [selectedDate, setSelectedDate] = useState('');

  const [tithi, setTithi] = useState<string | undefined>('');

  // State for Astronomy Data - Today
  const [astronomyDataToday, setAstronomyDataToday] = useState<AstronomyData>({
    sunriseTime: undefined,
    sunsetTime: undefined,
  });
  // State for Astronomy Data - Tomorrow
  const [astronomyDataTomorrow, setAstronomyDataTomorrow] =
    useState<AstronomyData>({
      sunriseTime: undefined,
      sunsetTime: undefined,
    });

  // State for describing location
  // Note: This will ensure if city conflicts with different locality across world
  const [locality, setLocality] = useState<Locality>({
    city: 'Mumbai',
    region: 'Maharashtra',
    country: 'India',
  });

  // State for making decision based on application data is fetched
  const [, setLoading] = useState(true);
  const [fetched, setFetched] = useState(false);

  // State for displaying night time choghadiya
  const [isNightChecked, setIsNightChecked] = useState(false);

  // setup today and tomorrow state whenever selectedDate updates
  const setupDates = (date?: string) => {
    const presentDate = date ? new Date(date) : new Date();
    const tomorrowDate = new Date(presentDate);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    setToday(presentDate.toISOString().split('T')[0]);
    setTomorrow(tomorrowDate.toISOString().split('T')[0]);
  };

  useEffect(() => {
    setupDates(selectedDate);
  }, [selectedDate]);

  const [timingsArr, setTimingsArr] = useState<string[]>([]);

  // Get the value of the 'location' query parameter or default to 'Mumbai' city.
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const city = params.get('location') || '';
  const [originalCity, setOriginalCity] = useState(
    normalizeLocation(city.toLowerCase()) || 'Mumbai',
  );

  // Is the user coming as referrer from known location
  const [isReferrer, setIsReferrer] = useState(false);
  useEffect(() => {
    const referrer = params.get('referrer') || '';

    if (referrer === 'pr_choghadiya') {
      setIsReferrer(true);
    }
  }, []);

  useEffect(() => {
    const fetchData = async (location: string, date: string) => {
      try {
        // if already known city with incorrect data use knownCityMap
        const getCityCoordinates = (input: string): string | undefined => {
          const city = input.toLowerCase();
          const coordinates = knownCityMap.get(city.toLowerCase());
          return coordinates;
        };

        // Fetch data based on location and date
        const response = await fetch(
          `/api/choghadiya?location=${
            getCityCoordinates(location) || location
          }&date=${date}`,
        );
        if (!response.ok) {
          throw new Error('API request failed'); // Throw an error for non-successful response
        }
        const data = await response.json();

        if (date === today) {
          setAstronomyDataToday({
            sunriseTime: data?.astronomy?.astro?.sunrise,
            sunsetTime: data?.astronomy?.astro?.sunset,
          });
        } else if (date === tomorrow) {
          setAstronomyDataTomorrow({
            sunriseTime: data?.astronomy?.astro?.sunrise,
            sunsetTime: data?.astronomy?.astro?.sunset,
          });
        }

        // Placeholder to inform users more information like region, country to ensure they are seeing expected data
        // This should handle misinformation for multiples cities with same name
        setLocality({
          city: originalCity,
          region: data?.location?.region,
          country: data?.location?.country,
        });

        // Update state when loading is completed (false) and fetching data is completed (true)
        setLoading(false);
        setFetched(true);

        // Setup URL to include city and date to provide users to bookmark and share
        const currentUrl = new URL(window.location.href);

        if (!currentUrl.searchParams.has('location')) {
          // Add the 'location' parameter to the URL
          currentUrl.searchParams.append('location', originalCity);
        } else {
          const prevLocation = currentUrl.searchParams.get('location');
          if (prevLocation !== originalCity) {
            currentUrl.searchParams.set('location', originalCity);
          }
        }
        if (!currentUrl.searchParams.has('date')) {
          // Add the 'date' parameter to the URL
          currentUrl.searchParams.append('date', today);
        } else {
          const prevDate = currentUrl.searchParams.get('date');
          if (prevDate !== today) {
            currentUrl.searchParams.set('date', today);
          }
        }

        // Update the browser's URL with the modified search params
        window.history.pushState({}, '', currentUrl);
      } catch (error) {
        console.error(error); // TODO: FUTURE - update as user-friendly message
        setLoading(false); // set loading to false as fetching is completed and we found error
        setFetched(false); // Set fetched to false in case of an error
      }
    };

    fetchData(originalCity, today); // Day time choghadiya does not depend on next day sunrise time
    if (isNightChecked) {
      fetchData(originalCity, tomorrow); // Night time choghadiya depends on next day sunrise time
    }
  }, [today, tomorrow, originalCity, isNightChecked]);

  const getDifferenceInMinutes = (time1: string, time2: string) => {
    const t1: number = parseTimeString(time1);
    const t2: number = parseTimeString(time2);
    const differenceInMilliseconds = t2 - t1;
    return differenceInMilliseconds / (1000 * 60);
  };

  useEffect(() => {
    if (!isNightChecked) {
      // day time choghadiya
      if (astronomyDataToday.sunriseTime && astronomyDataToday.sunsetTime) {
        const differenceInMinutes = getDifferenceInMinutes(
          astronomyDataToday.sunriseTime,
          astronomyDataToday.sunsetTime,
        );
        const dividedDifference: number = differenceInMinutes / 8;

        // calculate timings array for all day time choghadiya
        const choghadiyaArr = [
          astronomyDataToday.sunriseTime,
          ...Array.from({ length: 7 }, (_, i) => {
            const sunriseTime = astronomyDataToday.sunriseTime;
            if (sunriseTime) {
              return addMinutesToTime(dividedDifference * (i + 1), sunriseTime);
            }
            return '';
          }),
          astronomyDataToday.sunsetTime,
        ];
        setTimingsArr(choghadiyaArr);
      }
    } else {
      // night time choghadiya
      const minBeforeMidnight = '11:59 PM';
      const midnight = '12:00 AM';
      if (astronomyDataToday.sunsetTime && astronomyDataTomorrow.sunriseTime) {
        const differenceInMinutes1 =
          getDifferenceInMinutes(
            astronomyDataToday.sunsetTime,
            minBeforeMidnight,
          ) + 1; // +1 because we are excluding minute before midnight for simpler calculation
        const differenceInMinutes2 = getDifferenceInMinutes(
          midnight,
          astronomyDataTomorrow.sunriseTime,
        );
        const dividedDifference: number =
          (differenceInMinutes1 + differenceInMinutes2) / 8;

        // calculate timings array for all night time choghadiya
        const choghadiyaArr = [
          astronomyDataToday.sunsetTime,
          ...Array.from({ length: 7 }, (_, i) => {
            const sunsetTime = astronomyDataToday.sunsetTime;
            if (sunsetTime) {
              return addMinutesToTime(dividedDifference * (i + 1), sunsetTime);
            }
            return '';
          }),
          astronomyDataTomorrow.sunriseTime,
        ];
        setTimingsArr(choghadiyaArr);
      }
    }
  }, [astronomyDataToday, astronomyDataTomorrow, isNightChecked]);

  // User Entered Input City
  const handleSubmit = (city: string) => {
    reset();
    const normalizeCity = normalizeLocation(city.toLowerCase());
    setOriginalCity(normalizeCity);
    setLocality({
      city: normalizeCity,
      region: '',
      country: '',
    });

    // Send the user input event to Google Analytics
    gtag('event', 'Text_Entered', {
      event_category: 'User_Input',
      event_label: normalizeCity,
      event_value: normalizeCity,
      non_interaction: true,
    });
  };

  // Handler for night time choghadiya
  const handleNightCheckboxChange = () => {
    reset();
    setIsNightChecked(!isNightChecked);
  };

  const reset = () => {
    setLoading(true);
    setFetched(false);
    setTimingsArr([]); // When date is updated, set timings array empty.
  };

  // TODO FUTURE: Implement Debouncing to delay the network call until the user stops interacting with the input for a certain period
  // Handler for Date Change - Update selected date
  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const date = event.target.value;
    setSelectedDate(date);
    setTithi(calendarDays.get(selectedDate)?.tithi);
    reset();
  };

  return (
    <div className="container">
      <Container
        maxWidth="md"
        sx={{
          px: {
            xs: 0, // Set padding to 0 for extra small screens (<= 540px)
            md: 2, // Set default padding for medium screens (> 540px)
          },
        }}
      >
        <div className="box">
          {/* Heading */}
          <Typography variant="h4" className="heading" color="primary">
            Choghadiya Timings
          </Typography>
          {/* User Input Date and City Name */}
          <div className="choghadiya-input">
            <DateInput onDateChange={handleDateChange} today={today} />
            <AnotherCity
              handleSubmit={handleSubmit}
              originalCity={originalCity}
            />
          </div>
          {/* Location Info */}
          <Typography variant="h5">
            Location:{' '}
            {[locality?.city, locality?.region, locality?.country]
              .filter(Boolean)
              .join(', ')}
          </Typography>
          <Typography variant="h5">
            {calendarDays.get(selectedDate)?.tithi
              ? `Tithi: ${calendarDays.get(selectedDate)?.tithi}`
              : calendarDays.get(today)?.tithi &&
                `Tithi: ${calendarDays.get(today)?.tithi}`}
          </Typography>
          {/* Choghadiya timings table */}
          <Container maxWidth="sm">
            <Timings
              isNightChecked={isNightChecked}
              timingsArr={timingsArr}
              today={today}
            />
          </Container>
          {/* Night Time Choghadiya Checkbox */}
          <Night
            isNightChecked={isNightChecked}
            handleCheckboxChange={handleNightCheckboxChange}
          />
          <Share
            className={!fetched ? 'disabled' : ''}
            date={selectedDate}
            isNightChecked={isNightChecked}
            locality={locality}
            originalCity={originalCity}
            timingsArr={timingsArr}
            today={today}
            tithi={tithi}
          />
          {/* Promotion - Pachkhan App */}
          {!isReferrer && <Promotion originalCity={originalCity} />}
          {/* Company Footer */}
          <div className="container">
            <Container
              maxWidth="sm"
              sx={{
                px: {
                  xs: 0,
                  md: 2,
                },
              }}
            >
              <Footer />
            </Container>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Choghadiya;
